import type { FeatureHudPort } from "./FeatureHudPort";
import type { FeatureStateStore } from "../feature-state/FeatureStateStore";
import type { InteractionBlockStore } from "../interaction-block/InteractionBlockStore";
import type { MoneyContext } from "../money/MoneyContext";
import { PresentationTaskRunner } from "@slot/presentation";
import type { Log } from "@slot/presentation";

const COUNT_ANIM = {
  minSeconds: 0.18,
  maxSeconds: 0.45,
  perDigitSeconds: 0.03,
  easing: "quadOut" as const,
};

const SCOPE_ID = "feature_hud_win";

export class FeatureHudPresenter {
  private readonly panel: FeatureHudPort;
  private readonly log: Log;
  private readonly featureStateStore: FeatureStateStore;
  private readonly interactionBlockStore: InteractionBlockStore;
  private readonly moneyContext: MoneyContext;
  private readonly runner: PresentationTaskRunner;

  private unsubscribeFeatureState?: () => void;
  private lastDisplayedWinMinor: string = "0";
  /**
   * When true, the subscriber paints `lastDisplayedWinMinor` instead of the
   * store's `featureAccumulatedWinMinor`. Set during the deferral window
   * (between an `update` event arriving and `runPendingCountUp` consuming
   * it) AND while the count-up tween is in flight. Cleared once the tween
   * completes (or trivially returns).
   */
  private displayedWinFrozen = false;
  /**
   * Update phases stash their target here instead of animating immediately.
   * `runPendingCountUp()` is invoked from the `spin_boundary_end` chain in
   * the feature-HUD plugin (fire-and-forget) so the count-up tween runs
   * AFTER the spin's wins/cascades have been visualised — the accumulated
   * total flushes WITH the boundary-end, not before the wins that produced
   * it.
   */
  private pendingCountUpTarget: string | null = null;

  public constructor(input: {
    panel: FeatureHudPort;
    log: Log;
    featureStateStore: FeatureStateStore;
    interactionBlockStore: InteractionBlockStore;
    moneyContext: MoneyContext;
    runner: PresentationTaskRunner;
  }) {
    this.panel = input.panel;
    this.log = input.log;
    this.featureStateStore = input.featureStateStore;
    this.interactionBlockStore = input.interactionBlockStore;
    this.moneyContext = input.moneyContext;
    this.runner = input.runner;
  }

  public start(): void {
    this.unsubscribeFeatureState = this.featureStateStore.subscribe((snapshot) => {
      if (!snapshot.activeFeatureId) {
        this.panel.hide();
        return;
      }

      const spinsLeftText =
        snapshot.featureCountRemaining != null
          ? `${snapshot.featureCountRemaining}`
          : "";

      const winText = this.displayedWinFrozen
        ? this.moneyContext.formatter.formatMinor(this.lastDisplayedWinMinor)
        : this.moneyContext.formatter.formatMinor(snapshot.featureAccumulatedWinMinor ?? "0");

      this.panel.show(snapshot.activeFeatureId, winText, spinsLeftText);
    });
  }

  public stop(): void {
    this.unsubscribeFeatureState?.();
    this.unsubscribeFeatureState = undefined;
  }

  public async applyFeatureState(input: {
    featureIdentifier: string;
    featurePhase?: "enter" | "exit" | "update";
    featureCountRemaining?: string;
    featureAccumulatedWinMinor?: string;
  }): Promise<void> {
    const id = input.featureIdentifier;
    const phase = input.featurePhase;

    if (phase === "exit") {
      this.displayedWinFrozen = false;
      this.lastDisplayedWinMinor = "0";
      this.pendingCountUpTarget = null;
      this.featureStateStore.clear();
      this.interactionBlockStore.remove("feature_bonus");
      this.log(`[FeatureHUD] exit ${id}`);
      return;
    }

    if (phase === "update") {
      const current = this.featureStateStore.getSnapshot();

      if (!current.activeFeatureId) {
        // An update with no feature on the HUD snaps to its state. No round reaches it today: the
        // feature-flow validator refuses an update before its enter, and a restore replays the round
        // from its first event. It stays as the template's fallback for a round that starts inside a
        // running feature; unlike enter, it adds no `feature_bonus` block.
        this.featureStateStore.setActiveFeature({
          featureId: id,
          featureCountRemaining: input.featureCountRemaining ?? null,
          featureAccumulatedWinMinor: input.featureAccumulatedWinMinor ?? "0",
        });
        this.lastDisplayedWinMinor = input.featureAccumulatedWinMinor ?? "0";
      } else {
        // Set the deferral flags BEFORE updateProgress so the synchronous
        // store emit sees `displayedWinFrozen=true` and keeps showing
        // `lastDisplayedWinMinor`. Without this ordering, the subscriber
        // would paint the new total mid-spin before the count-up runs.
        this.pendingCountUpTarget = input.featureAccumulatedWinMinor ?? "0";
        this.displayedWinFrozen = true;

        this.featureStateStore.updateProgress({
          featureCountRemaining: input.featureCountRemaining,
          featureAccumulatedWinMinor: input.featureAccumulatedWinMinor,
        });
      }

      this.log(
        `[FeatureHUD] update ${id} spins=${input.featureCountRemaining ?? ""} total=${input.featureAccumulatedWinMinor ?? ""}`,
      );
      return;
    }

    // "enter" phase — snap, no animation.
    this.displayedWinFrozen = false;
    this.pendingCountUpTarget = null;
    this.lastDisplayedWinMinor = input.featureAccumulatedWinMinor ?? "0";
    this.featureStateStore.setActiveFeature({
      featureId: id,
      featureCountRemaining: input.featureCountRemaining ?? null,
      featureAccumulatedWinMinor: input.featureAccumulatedWinMinor ?? "0",
    });
    this.interactionBlockStore.add("feature_bonus");

    this.log(
      `[FeatureHUD] show ${id} spins=${input.featureCountRemaining ?? ""} total=${input.featureAccumulatedWinMinor ?? ""}`,
    );
  }

  /**
   * Runs the deferred count-up animation, if any. Invoked fire-and-forget
   * from the spin-boundary end so the tween
   * starts as the current spin's boundary closes — after wins/cascades
   * have played out, before the next spin's dropout. Returns a Promise so
   * callers can await if they need sequential behaviour. No-op if there's
   * nothing pending.
   */
  public async runPendingCountUp(): Promise<void> {
    const target = this.pendingCountUpTarget;
    if (target === null) return;
    this.pendingCountUpTarget = null;
    await this.animateWinCountUp(target);
  }

  private async animateWinCountUp(targetMinor: string): Promise<void> {
    const start = BigInt(this.lastDisplayedWinMinor);
    const end = BigInt(targetMinor);

    if (end === start) {
      // Nothing to animate — clear the gating flag so the subscriber can
      // paint store-driven updates again.
      this.lastDisplayedWinMinor = targetMinor;
      this.displayedWinFrozen = false;
      return;
    }

    const duration = this.computeDuration(start, end);
    const diff = end - start;
    const denom = 1000n;

    await this.runner.tweenProgress(
      duration,
      (t01) => {
        const t = Math.max(0, Math.min(1, t01));
        const tn = BigInt(Math.round(t * 1000));
        const cur = start + (diff * tn) / denom;
        this.panel.setAccumulatedWinText(
          this.moneyContext.formatter.formatMinor(cur.toString()),
        );
      },
      SCOPE_ID,
      { easing: COUNT_ANIM.easing },
    );

    // Commit exact final value
    this.lastDisplayedWinMinor = targetMinor;
    this.displayedWinFrozen = false;
    this.panel.setAccumulatedWinText(
      this.moneyContext.formatter.formatMinor(targetMinor),
    );
  }

  private computeDuration(start: bigint, end: bigint): number {
    const delta = end >= start ? end - start : start - end;
    const digits = delta === 0n ? 1 : delta.toString().length;
    const raw = COUNT_ANIM.minSeconds + digits * COUNT_ANIM.perDigitSeconds;
    return Math.max(COUNT_ANIM.minSeconds, Math.min(COUNT_ANIM.maxSeconds, raw));
  }
}
